import React, { useState, useEffect } from 'react';
import { ArrowLeft, Info, ChevronDown, ListOrdered } from 'lucide-react';
import { useNavigate, Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ContainerPagination from '../components/ContainerPagination';
import DocsBreadcrumb from '../components/DocsBreadcrumb';
import KeyboardShortcutsOverlay from '../components/KeyboardShortcutsOverlay';
import BackToTop from '../components/BackToTop';

const bgImage = 'https://customer-assets.emergentagent.com/job_3f9e40e3-cca9-4507-83e7-20ced098354a/artifacts/69qn1rnj_ABSTRACT%20DESIGN.png';

const TOC = [
  { id: 'overview', label: 'Overview' },
  { id: 'process', label: '3D Secure Process' },
  { id: 'process-diagram', label: 'Flow Diagram' },
  { id: 'catering', label: 'Catering 3D Secure', children: [
    { id: 'required-fields', label: 'Required Form Fields' },
    { id: 'sample-form', label: 'Sample 3D Secure Form' },
  ]},
  { id: 'expected-response', label: 'Expected 3D Secure Response' },
  { id: 'next-step', label: 'Next Step — Confirm' },
];

const flatIds = TOC.flatMap((i) => [i.id, ...(i.children?.map((c) => c.id) || [])]);

const SAMPLE_FORM = `<!-- Fill Values From The Sale Response -->
<!DOCTYPE html>
<html>
    <head>
        <meta charset="UTF-8">
        <title></title>
    </head>

    <body onload="document.stepUpForm.submit();">
        <form name="stepUpForm" method="post" action="https://staging.ecomm365.com/SecureCustomerAuthentication/API/StepUP">
            <input type="hidden" name="ReturnUrl" value="{ merchant's Handler URL }" />
            <input type="hidden" name="Payload" value="{ value from field R4 in sale response }" />
            <input type="hidden" name="CardinalTransactionId" value="{ value from field R7 in sale response }" />
            <input type="hidden" name="ReferenceID" value="{ value from field R6 in sale response }" />
            <input type="hidden" name="MD" value="{ value from field R5 in sale response }" />
            <input type="hidden" name="ACSUrl" value="{ value from field R3 in sale response }" />
            <input type="hidden" name="VendorId" value="{ merchant's VID }" />
        </form>
    </body>
</html>`;

const EXPECTED_RESPONSE = `<!-- Sample XML Document If The Card Is 3D Secure -->
<?xml version="1.0" encoding="utf-8"?>
<R xmlns:xsd="http://www.w3.org/2001/XMLSchema" xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance">
    <R1>0000</R1>
    <R2>Successful</R2>
    <R3>https://1merchantacsstag.cardinalcommerce.com/MerchantACSWeb/creq.jsp</R3>
    <R4>eyJtZXNzYWdlVHlwZSI6IkNSZXEiLCJtZXNzYWdlVmVyc2lvbil6IjluMi4wliwidGhyZWVEU1NlcnZlclRyYW5zSUQiOilzZmE2MDJiMy0yOTE5LTRmY2ItYTZIZS1jMTY3Y2U5ZDVmOTUi...</R4>
    <R5>7b47ecb6-058e-4355-a0c1-734c2d2fda65</R5>
    <R6>61c5580b-dab9-4305-bc26-b8f9d2212b6f</R6>
    <R7>6gUaDtGDeg96vk3pJrg1</R7>
    <CardHolderInfo />
</R>`;

const REQUIRED_FIELDS = [
  { name: 'ReturnUrl', desc: "The Merchant's Handler URL — where the ACS will redirect after authentication (TermURL).", source: 'Merchant-provided' },
  { name: 'VendorId', desc: "The merchant's Vendor ID (VID) issued by eCOMM.", source: 'Merchant-provided' },
  { name: 'ACSUrl', desc: 'URL of the 3D Secure service (Access Control Server) — submit the form to this URL.', source: 'R3 from Sale response' },
  { name: 'Payload', desc: 'The CReq JWT payload passed to the Issuer ACS.', source: 'R4 from Sale response' },
  { name: 'MD', desc: 'Secret key exchanged between Issuer, Acquirer and Merchant to reference this transaction.', source: 'R5 from Sale response' },
  { name: 'ReferenceID', desc: 'Cardinal reference ID tying the authentication to the Sale.', source: 'R6 from Sale response' },
  { name: 'CardinalTransactionId', desc: 'Cardinal transaction ID for the authentication session.', source: 'R7 from Sale response' },
];

const CodeBlock = ({ code, lang = 'html', testid }) => (
  <div className="relative bg-[#1e1e1e] rounded-lg overflow-hidden border border-gray-700 my-4" data-testid={testid}>
    <div className="flex items-center justify-between px-4 py-2 bg-[#2d2d2d] border-b border-gray-700">
      <span className="text-xs font-mono text-gray-400 uppercase tracking-wide">{lang}</span>
      <button
        onClick={() => navigator.clipboard?.writeText(code)}
        className="text-xs text-gray-400 hover:text-white transition-colors duration-150 font-medium"
        data-testid={`${testid}-copy`}
      >
        Copy
      </button>
    </div>
    <pre className="overflow-x-auto p-4 text-sm leading-relaxed">
      <code className={`language-${lang} text-gray-100 font-mono whitespace-pre`}>{code}</code>
    </pre>
  </div>
);

const ThreeDSecureAuth = () => {
  const navigate = useNavigate();
  const [activeId, setActiveId] = useState(flatIds[0]);
  const [mobileTocOpen, setMobileTocOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      const offset = 140;
      let current = flatIds[0];
      for (const id of flatIds) {
        const el = document.getElementById(id);
        if (el && el.getBoundingClientRect().top - offset <= 0) current = id;
      }
      setActiveId(current);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNavClick = (e, id) => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (el) {
      const y = el.getBoundingClientRect().top + window.scrollY - 100;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
    setMobileTocOpen(false);
  };

  return (
    <div className="min-h-screen flex flex-col scroll-smooth" data-testid="threeds-auth-page">
      <Header />
      <div className="h-20" />

      <div className="relative flex-1">
        <div
          className="fixed inset-0 top-20 bg-cover bg-center"
          style={{ backgroundImage: `url(${bgImage})` }}
        />

        <div className="relative z-10 max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-10">
          <DocsBreadcrumb section="api-reference" currentId="3d-secure-auth" />
          <button
            onClick={() => navigate('/technical-documentation/api-reference')}
            className="flex items-center gap-2 text-[#1F4E79] hover:text-[#163a5f] font-medium mb-8 transition-colors duration-200"
            data-testid="threeds-auth-back-button"
          >
            <ArrowLeft size={18} />
            Back to API Reference
          </button>

          <div className="flex flex-col lg:flex-row gap-8">
            <main className="flex-1 min-w-0 lg:order-1">
              {/* Mobile collapsed TOC pill */}
              <div className="lg:hidden mb-6 border border-gray-200 rounded-xl overflow-hidden bg-white/90 backdrop-blur-sm">
                <button
                  type="button"
                  onClick={() => setMobileTocOpen((o) => !o)}
                  className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 text-left"
                  aria-expanded={mobileTocOpen}
                  data-testid="mobile-toc-toggle"
                >
                  <span className="flex items-center gap-2 text-sm font-semibold text-[#0a1929]">
                    <ListOrdered size={16} className="text-[#ef5b25]" />
                    On this page
                    <span className="text-gray-400 font-normal">({flatIds.length})</span>
                  </span>
                  <ChevronDown
                    size={18}
                    className={`text-gray-500 transition-transform ${mobileTocOpen ? 'rotate-180' : ''}`}
                  />
                </button>
                {mobileTocOpen && (
                  <nav className="bg-white max-h-[60vh] overflow-y-auto py-2" data-testid="mobile-toc-panel">
                    {TOC.map((item) => (
                      <div key={item.id}>
                        <a
                          href={`#${item.id}`}
                          onClick={(e) => handleNavClick(e, item.id)}
                          className={`block px-4 py-2 text-sm transition-colors duration-150 border-l-2 ${
                            activeId === item.id
                              ? 'border-[#1F4E79] bg-blue-50 text-[#1F4E79] font-semibold'
                              : 'border-transparent text-gray-700 hover:bg-gray-50 hover:text-[#1F4E79]'
                          }`}
                          data-testid={`m-toc-link-${item.id}`}
                        >
                          {item.label}
                        </a>
                        {item.children && (
                          <div className="ml-3">
                            {item.children.map((child) => (
                              <a
                                key={child.id}
                                href={`#${child.id}`}
                                onClick={(e) => handleNavClick(e, child.id)}
                                className={`block px-4 py-1.5 text-xs transition-colors duration-150 border-l-2 ${
                                  activeId === child.id
                                    ? 'border-[#1F4E79] bg-blue-50/60 text-[#1F4E79] font-semibold'
                                    : 'border-transparent text-gray-600 hover:bg-gray-50 hover:text-[#1F4E79]'
                                }`}
                                data-testid={`m-toc-link-${child.id}`}
                              >
                                {child.label}
                              </a>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </nav>
                )}
              </div>

              {/* Title */}
              <div className="bg-white/80 backdrop-blur-sm rounded-lg shadow-lg p-8 sm:p-12 mb-8">
                <div className="flex items-center gap-3 mb-3">
                  <h1 className="text-4xl sm:text-5xl font-bold text-gray-900" data-testid="threeds-auth-title">
                    3D Secure Auth
                  </h1>
                  <span className="inline-flex items-center px-2 py-1 rounded text-xs font-bold bg-[#1F4E79] text-white uppercase tracking-wide">
                    3DS v2
                  </span>
                </div>
                <p className="text-gray-600 text-lg">
                  EMV 3DS — a challenge-only layer on top of Sale / Pre-Auth requests where the Issuer verifies the cardholder before authorising the transaction.
                </p>
                <div className="mt-4 flex flex-wrap items-center gap-2" data-testid="api-signature-chips">
                  <span className="text-[10px] font-bold text-gray-500 uppercase tracking-[0.15em]">API Signature</span>
                  <code
                    className="inline-flex items-center px-2.5 py-1 rounded-md bg-gray-900 text-[#85ea2d] text-xs font-mono font-semibold shadow-sm border border-gray-800"
                    data-testid="api-signature-sale"
                  >
                    APISignature=sale
                  </code>
                  <span className="text-[10px] text-gray-500 italic">
                    (same as Sale — 3DS is returned in the Sale response)
                  </span>
                </div>
              </div>

              {/* Overview */}
              <section id="overview" className="scroll-mt-28 bg-white/80 backdrop-blur-sm rounded-lg shadow-lg p-8 sm:p-10 mb-6">
                <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">Overview</h2>
                <div className="space-y-4 text-gray-700 text-base leading-relaxed">
                  <p>
                    3D Secure enables consumers to verify their identity with the card issuer before a transaction can be completed. If accepted, the consumer can then complete their order — and once received by the merchant, the merchant has higher confidence that the transaction is genuine and not fraud.
                  </p>
                  <p>
                    The 3D Secure protocol was developed by Visa to improve the security of Internet payments. The protocol is offered with the service name <strong>Verified by Visa</strong>. MasterCard has also adapted a similar protocol called <strong>MasterCard SecureCode</strong>. Both are designed to allow authentication of cardholders by their Issuers at participating merchants.
                  </p>
                  <p>
                    This section is only required when you initially request content using our <Link to="/technical-documentation/api-reference/sale-pre-auth" className="text-[#1F4E79] underline hover:text-[#163a5f]">Sale</Link> API — our <span className="text-gray-400 italic">Hosted Payments Page</span> API handles the 3D Secure for you. If the card being used is 3D Secure, the response will return 3D Secure data that must be used while catering for 3D Secure. This information will be used in numerous steps to ensure completion of the 3D Secure process; the 3D Secure catering process is explained in depth below.
                  </p>
                </div>
                <div className="bg-blue-50/80 border border-blue-200 rounded-lg p-4 mt-5 flex items-start gap-3">
                  <Info size={18} className="text-[#1F4E79] flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-gray-700">
                    <strong>Good to know:</strong> 3D Secure 2.x (EMV 3DS) also supports <em>frictionless</em> authentication where the Issuer approves the transaction silently based on risk data — meaning no challenge is presented and the Confirm step can be skipped. Always build your flow to handle both frictionless and challenge paths.
                  </p>
                </div>
              </section>

              {/* Process */}
              <section id="process" className="scroll-mt-28 bg-white/80 backdrop-blur-sm rounded-lg shadow-lg p-8 sm:p-10 mb-6">
                <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">3D Secure Process</h2>
                <p className="text-gray-700 text-base leading-relaxed mb-4">
                  The process of 3D Secure is simple and straightforward. Below demonstrates the steps required in order to successfully cater for 3D Secure.
                </p>
                <ol className="list-decimal pl-6 space-y-3 text-gray-700 text-base leading-relaxed">
                  <li>
                    Sale request returns with <code className="bg-gray-100 px-1.5 py-0.5 rounded text-sm font-mono">0000</code> inside the <code className="bg-gray-100 px-1.5 py-0.5 rounded text-sm font-mono">&lt;R1&gt;</code> field which indicates you must cater for 3D Secure — failure will decline the transaction.
                  </li>
                  <li>
                    Using data returned in the Sale response, you must now construct an <strong>HTML Form</strong> containing the required information and have the form automatically submit on page load.
                  </li>
                  <li>
                    This form will submit the data and load the 3D Secure page where the consumer must enter their unique secure code and submit in order to authenticate themselves.
                  </li>
                  <li>
                    The form will then redirect the consumer to your <strong>TermURL</strong> where the merchant will have their <span className="text-gray-400 italic">3D Secure Confirm</span> API set up to confirm the consumer has passed authentication.
                  </li>
                  <li>
                    The response from the 3D Secure Confirm API within your TermURL will respond with the usual error code / description result that would normally determine a successful or declined transaction. The result may also be sent to your notification handler if set up to do so. Please read the <Link to="/technical-documentation/api-reference/sale-pre-auth#expected-responses" className="text-[#1F4E79] underline hover:text-[#163a5f]">Sale documentation</Link> about the potential error codes &amp; descriptions that are available.
                  </li>
                </ol>
              </section>

              {/* Flow Diagram — value-add */}
              <section id="process-diagram" className="scroll-mt-28 bg-white/80 backdrop-blur-sm rounded-lg shadow-lg p-8 sm:p-10 mb-6">
                <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">Flow Diagram</h2>
                <p className="text-gray-700 text-base leading-relaxed mb-6">
                  Visualising the sequence helps new integrators grasp the flow at a glance — Consumer → Merchant → eCOMM → Issuer ACS.
                </p>
                <div className="rounded-lg border border-gray-200 bg-gradient-to-br from-gray-50 to-white p-6 overflow-x-auto">
                  <div className="min-w-[720px]">
                    {/* Horizontal flow */}
                    <div className="grid grid-cols-5 gap-3 items-stretch text-center">
                      {[
                        { n: '1', t: 'Sale Request', d: 'Merchant submits sale → eCOMM', color: 'bg-[#1F4E79] text-white' },
                        { n: '2', t: 'R1=0000 Response', d: 'eCOMM returns 3DS params (R3–R7)', color: 'bg-[#2d6ca8] text-white' },
                        { n: '3', t: 'Challenge Form', d: 'Merchant auto-submits stepUp form to ACS', color: 'bg-[#85ea2d] text-gray-900' },
                        { n: '4', t: 'Issuer ACS', d: 'Cardholder enters OTP / biometric', color: 'bg-[#2d6ca8] text-white' },
                        { n: '5', t: 'Redirect to TermURL', d: 'PaRes returned to merchant', color: 'bg-[#1F4E79] text-white' },
                      ].map((step, i) => (
                        <div key={step.n} className="flex flex-col items-center relative">
                          <div className={`${step.color} rounded-lg p-4 w-full h-full flex flex-col justify-between shadow-sm`}>
                            <div className="text-2xl font-bold leading-none mb-2">{step.n}</div>
                            <div className="text-sm font-bold mb-1">{step.t}</div>
                            <div className="text-xs opacity-90 leading-snug">{step.d}</div>
                          </div>
                          {i < 4 && (
                            <div className="hidden md:block absolute top-1/2 -right-2 z-10 text-2xl text-gray-400 pointer-events-none">→</div>
                          )}
                        </div>
                      ))}
                    </div>
                    <div className="mt-5 pt-5 border-t border-gray-200">
                      <p className="text-sm text-gray-600 text-center">
                        Next: call <Link to="/technical-documentation/api-reference/3d-secure-confirm" className="text-[#1F4E79] underline font-semibold">3D Secure Confirm</Link> with the returned PaRes → receive <code className="bg-gray-100 px-1 rounded">0044</code> (success) or appropriate error code.
                      </p>
                    </div>
                  </div>
                </div>
              </section>

              {/* Catering 3D Secure */}
              <section id="catering" className="scroll-mt-28 bg-white/80 backdrop-blur-sm rounded-lg shadow-lg p-8 sm:p-10 mb-6">
                <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">Catering 3D Secure</h2>
                <div className="space-y-4 text-gray-700 text-base leading-relaxed mb-6">
                  <p>
                    When a card is 3D Secure, you must load a form inside an <strong>iFrame</strong> which will be the 3D Secure handler. You must construct the form with the required values outlined below and have it automatically submitted to the authenticator URL set in the form's <code className="bg-gray-100 px-1.5 py-0.5 rounded text-sm font-mono">action</code> attribute.
                  </p>
                  <p>
                    You must construct the form, populated with the values received in the <Link to="/technical-documentation/api-reference/sale-pre-auth#response-3ds" className="text-[#1F4E79] underline hover:text-[#163a5f]">Sale Response</Link>.
                  </p>
                </div>

                {/* Required Fields table */}
                <div id="required-fields" className="scroll-mt-28 mb-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Required Form Fields</h3>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm border-collapse" data-testid="threeds-required-fields">
                      <thead>
                        <tr className="bg-[#1F4E79] text-white">
                          <th className="text-left p-3 w-52">Form Field</th>
                          <th className="text-left p-3">Description</th>
                          <th className="text-left p-3 w-48">Source</th>
                        </tr>
                      </thead>
                      <tbody>
                        {REQUIRED_FIELDS.map((f, i) => (
                          <tr key={f.name} className={`border-b border-gray-200 ${i % 2 ? 'bg-gray-50/50' : ''}`}>
                            <td className="p-3 font-mono font-semibold text-[#1F4E79]">{f.name}</td>
                            <td className="p-3">{f.desc}</td>
                            <td className="p-3 text-xs text-gray-600">{f.source}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Sample form */}
                <div id="sample-form" className="scroll-mt-28">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Sample 3D Secure Form</h3>
                  <p className="text-sm text-gray-600 mb-2">
                    Copy-paste this template and replace the <code className="bg-gray-100 px-1 rounded">{'{ ... }'}</code> placeholders with the values returned in your Sale response.
                  </p>
                  <CodeBlock code={SAMPLE_FORM} lang="html" testid="sample-3ds-form" />
                </div>

                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mt-4">
                  <p className="text-sm font-semibold text-gray-800 mb-2">What Happens Next</p>
                  <p className="text-sm text-gray-700">
                    The <strong>PaRes</strong> will be returned to your TermURL page. The PaRes value should then be passed into the <code className="bg-white px-1.5 py-0.5 rounded font-mono">&lt;R1&gt;</code> field of the <span className="italic text-gray-500">3D Secure Confirm</span> request.
                  </p>
                </div>
              </section>

              {/* Expected Response */}
              <section id="expected-response" className="scroll-mt-28 bg-white/80 backdrop-blur-sm rounded-lg shadow-lg p-8 sm:p-10 mb-6">
                <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">Expected 3D Secure Response</h2>
                <p className="text-gray-700 text-base leading-relaxed mb-4">
                  As stated above, if your <code className="bg-gray-100 px-1.5 py-0.5 rounded text-sm font-mono">&lt;R1&gt;</code> within the Sale response is <code className="bg-gray-100 px-1.5 py-0.5 rounded text-sm font-mono">0000</code> and contains the other 3D Secure parameters listed above, you must then cater for 3D Secure. Below demonstrates a Sale response that indicates the card is 3D Secure.
                </p>
                <CodeBlock code={EXPECTED_RESPONSE} lang="xml" testid="expected-3ds-response" />
              </section>

              {/* Next Step — value-add */}
              <section id="next-step" className="scroll-mt-28 bg-gradient-to-br from-[#1F4E79] to-[#2d6ca8] rounded-lg shadow-lg p-8 sm:p-10 mb-6 text-white">
                <h2 className="text-2xl sm:text-3xl font-bold mb-3">Next Step — 3D Secure Confirm</h2>
                <p className="text-white/90 text-base leading-relaxed mb-5">
                  Once the cardholder has authenticated with their Issuer, your TermURL will receive the PaRes payload. The final step is to call <strong>3D Secure Confirm</strong> to complete the transaction and obtain the authorisation result.
                </p>
                <Link
                  to="/technical-documentation/api-reference/3d-secure-confirm"
                  className="inline-flex items-center gap-2 px-5 py-2.5 bg-white text-[#1F4E79] hover:bg-gray-100 font-semibold rounded-md shadow-sm transition-colors duration-150"
                  data-testid="next-step-link"
                >
                  Continue to 3D Secure Confirm
                  <span aria-hidden="true">→</span>
                </Link>
              </section>

              <ContainerPagination currentId="3d-secure-auth" />
            </main>

            {/* Sidebar */}
            <aside className="hidden lg:block lg:w-64 lg:flex-shrink-0 lg:order-2" data-testid="threeds-auth-sidebar">
              <div className="lg:sticky lg:top-28">
                <div className="bg-white/85 backdrop-blur-sm rounded-lg shadow-md border border-gray-200 overflow-hidden">
                  <div className="px-4 py-3 bg-[#1F4E79] text-white">
                    <p className="text-sm font-semibold tracking-wide">On This Page</p>
                  </div>
                  <nav className="py-2 max-h-[calc(100vh-10rem)] overflow-y-auto">
                    {TOC.map((item) => (
                      <div key={item.id}>
                        <a
                          href={`#${item.id}`}
                          onClick={(e) => handleNavClick(e, item.id)}
                          className={`block px-4 py-2 text-sm transition-colors duration-150 border-l-2 ${
                            activeId === item.id
                              ? 'border-[#1F4E79] bg-blue-50 text-[#1F4E79] font-semibold'
                              : 'border-transparent text-gray-700 hover:bg-gray-50 hover:text-[#1F4E79]'
                          }`}
                          data-testid={`toc-link-${item.id}`}
                        >
                          {item.label}
                        </a>
                        {item.children && (
                          <div className="ml-3">
                            {item.children.map((child) => (
                              <a
                                key={child.id}
                                href={`#${child.id}`}
                                onClick={(e) => handleNavClick(e, child.id)}
                                className={`block px-4 py-1.5 text-xs transition-colors duration-150 border-l-2 ${
                                  activeId === child.id
                                    ? 'border-[#1F4E79] bg-blue-50/60 text-[#1F4E79] font-semibold'
                                    : 'border-transparent text-gray-600 hover:bg-gray-50 hover:text-[#1F4E79]'
                                }`}
                                data-testid={`toc-link-${child.id}`}
                              >
                                {child.label}
                              </a>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </nav>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </div>

      <div className="relative z-10">
        <Footer />
      </div>
      <KeyboardShortcutsOverlay />
      <BackToTop />
    </div>
  );
};

export default ThreeDSecureAuth;
